// ============================================
// src/components/PushNotificationToggle.js
// Drop-in toggle card for Settings page
// ============================================

import React, { useState } from 'react';
import {
  Bell, BellOff, Loader2, CheckCircle2, AlertCircle, Send,
  TrendingUp, TrendingDown, ShoppingBag, Store
} from 'lucide-react';
import { usePushNotifications } from '../hooks/usePushNotifications';

// Formats today's date as "29 Dec 2025"
const formatDate = (date = new Date()) => {
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Preview card — shows exactly what the push notification body will look like
const NotificationPreview = ({ restaurantName, outletName }) => {
  const today = formatDate();
  const salesChange = +35;

  return (
    <div className="bg-slate-900 rounded-xl p-4 text-white text-sm font-mono space-y-1.5 shadow-inner">
      <p className="font-bold text-white text-base">📊 Daily Sales Update — {today}</p>
      <p className="text-slate-300">
        <span className="text-slate-400">Outlet: </span>
        {outletName || 'Wazwan, Panchkula'}
      </p>
      <div className="border-t border-slate-700 pt-1.5 space-y-1">
        <p className="text-slate-300">
          <span className="text-slate-400">Total Sales: </span>
          <span className="text-green-400 font-bold">₹9,065</span>
        </p>
        <p className="text-slate-300">
          <span className="text-slate-400">Total Orders: </span>8
        </p>
        <p className="text-slate-300 flex items-center gap-1">
          <span className="text-slate-400">vs Last {new Date().toLocaleDateString('en-IN', { weekday: 'long' })}: </span>
          {salesChange >= 0
            ? <span className="text-green-400 font-bold flex items-center gap-0.5"><TrendingUp size={12} />+{salesChange}%</span>
            : <span className="text-red-400 font-bold flex items-center gap-0.5"><TrendingDown size={12} />{salesChange}%</span>
          }
        </p>
      </div>
      <p className="text-slate-500 text-xs pt-1 border-t border-slate-700">— powered by TOBO</p>
    </div>
  );
};

const PushNotificationToggle = ({ token, restaurantName, outletName }) => {
  const {
    isSupported, isSubscribed, isLoading, error,
    subscribe, unsubscribe, sendTest,
  } = usePushNotifications(token);
  const [testStatus, setTestStatus] = useState(null); // 'sent' | 'error' | 'loading'

  const handleTest = async () => {
    setTestStatus('loading');
    try {
      const result = await sendTest();
      setTestStatus(result?.success ? 'sent' : 'error');
    } catch {
      setTestStatus('error');
    }
    setTimeout(() => setTestStatus(null), 4000);
  };

  if (!isSupported) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
        <AlertCircle size={18} className="text-amber-500 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-semibold text-amber-700">Push Notifications Unavailable</p>
          <p className="text-xs text-amber-600 mt-0.5">
            Your browser does not support push notifications. Try Chrome or Edge on desktop/Android.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm space-y-4">

      {/* ── Toggle row ─────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-xl ${isSubscribed ? 'bg-green-50' : 'bg-slate-50'}`}>
            {isSubscribed
              ? <Bell size={20} className="text-green-600" />
              : <BellOff size={20} className="text-slate-400" />
            }
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-800">Daily Sales Push Notification</h3>
            <p className="text-xs text-slate-500 mt-0.5">Sent at 11:59 PM IST every day</p>
          </div>
        </div>

        <button
          onClick={isSubscribed ? unsubscribe : subscribe}
          disabled={isLoading}
          className={`relative w-12 h-6 rounded-full transition-colors duration-200 focus:outline-none ${
            isSubscribed ? 'bg-green-500' : 'bg-slate-200'
          } ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          aria-label={isSubscribed ? 'Disable notifications' : 'Enable notifications'}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 flex items-center justify-center ${
            isSubscribed ? 'translate-x-6' : 'translate-x-0'
          }`}>
            {isLoading && <Loader2 size={10} className="animate-spin text-slate-400" />}
          </span>
        </button>
      </div>

      {/* ── Subscribed status ──────────────────────────────────── */}
      {isSubscribed && (
        <div className="bg-green-50 rounded-xl px-4 py-2.5 flex items-center gap-2">
          <CheckCircle2 size={14} className="text-green-600 shrink-0" />
          <p className="text-xs text-green-700 font-medium">
            You'll receive daily sales summaries at 11:59 PM IST.
          </p>
        </div>
      )}

      {/* ── Notification preview ─────────────────────────────────
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
          Notification Preview
        </p>
        <NotificationPreview restaurantName={restaurantName} outletName={outletName} />
      </div>

      <div className="grid grid-cols-2 gap-2">
        {[
          { icon: <ShoppingBag size={12} />, label: 'Total Sales (₹)' },
          { icon: <Store size={12} />, label: 'Outlet Name' },
          { icon: <TrendingUp size={12} />, label: 'Order Count' },
          { icon: <TrendingUp size={12} />, label: 'Week-on-week %' },
        ].map(({ icon, label }) => (
          <div key={label} className="flex items-center gap-1.5 text-xs text-slate-500 bg-slate-50 rounded-lg px-2.5 py-1.5">
            <span className="text-slate-400">{icon}</span>
            {label}
          </div>
        ))}
      </div> */}

      {/* ── Error ─────────────────────────────────────────────── */}
      {error && (
        <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-2.5 flex items-start gap-2">
          <AlertCircle size={14} className="text-red-500 mt-0.5 shrink-0" />
          <p className="text-xs text-red-600">{error}</p>
        </div>
      )}

      {/* ── Test button ────────────────────────────────────────── */}
      {isSubscribed && (
        <button
          onClick={handleTest}
          disabled={testStatus === 'loading'}
          className="flex items-center gap-2 text-xs font-semibold text-slate-500 hover:text-slate-700 transition-colors px-3 py-2.5 rounded-xl hover:bg-slate-50 w-full justify-center border border-slate-200 disabled:opacity-50"
        >
          {testStatus === 'loading'
            ? <Loader2 size={12} className="animate-spin" />
            : <Send size={12} />
          }
          {testStatus === 'sent'
            ? '✅ Test notification sent!'
            : testStatus === 'error'
            ? '❌ Failed to send test'
            : testStatus === 'loading'
            ? 'Sending...'
            : 'Send test notification'
          }
        </button>
      )}
    </div>
  );
};

export default PushNotificationToggle;